import { z } from 'zod';
import {
  CursorInputSchema,
  IdSchema,
  IdempotencyKeySchema,
  IsoDateSchema,
  IsoDateTimeSchema,
  MoneySchema,
  pageOf,
  withMeta,
} from '../common';
import {
  ExTradeRowSchema,
  JEntrySchema,
  KpiSchema,
  KvSchema,
  ReplayAiSchema,
  RevDaySchema,
} from '../dataset/core';
import { EodBypassSessionSchema, EodSessionSchema } from '../dataset/eod';
import { JournalDaySchema } from '../dataset/journal';
import { ApAccountSchema } from '../dataset/progress';
import { BtBookSchema, ReplayGradingSchema, ReplaySessionSchema } from '../dataset/review';
import { defineProcedures, mutation, query, type RouterShape } from '../procedure';
import { defineFamily } from './family';

/** review.* — Journal, Trade Replay (+ backtest R-03), End-of-Day Report, Account Progress (SDD §7.4 Review rows). */

// ── Journal: one entry model — session-linked record + free entries + trade notes; voice entries carry audio refs ──
export const JournalEntryModeSchema = z.enum(['text', 'voice']);
export const JournalEntryKindSchema = z.enum(['pre', 'post', 'free', 'trade', 'checklist']);
export const JournalEntrySchema = z
  .object({
    id: IdSchema,
    day: IsoDateSchema,
    kind: JournalEntryKindSchema,
    mode: JournalEntryModeSchema,
    text: z.string(),
    tags: z.array(z.string()),
    emotionTags: z.array(z.string()),
    sessionId: IdSchema.nullable(),
    tradeId: z.number().int().nullable(),
    audioRef: z.object({ id: IdSchema, durationS: z.number() }).strict().nullable(),
    screenshots: z.array(IdSchema),
    createdAt: IsoDateTimeSchema,
  })
  .strict();
export type JournalEntry = z.infer<typeof JournalEntrySchema>;
export const JournalListSchema = pageOf(JournalEntrySchema).extend({
  /** Reference journal days (calendar heatmap) keyed by the reference jrnKey() form. */
  days: z.record(z.string(), JournalDaySchema),
  /** Reviewed session days (REV_DAYS) and the trade-log entries the day view renders. */
  reviewedDays: z.array(RevDaySchema),
  tradeEntries: z.array(JEntrySchema),
});
export const JournalPatternSchema = z
  .object({
    key: z.string(),
    label: z.string(),
    occurrences: z.number().int(),
    effectR: z.number(),
    evidence: z.array(IdSchema),
  })
  .strict();

// ── Trade replay + backtest ──
export const ReplayBundleSchema = withMeta({
  bundleId: IdSchema,
  day: IsoDateSchema,
  state: z.enum(['preparing', 'ready']),
  session: ReplaySessionSchema,
  grading: ReplayGradingSchema,
  metaRows: z.array(KvSchema),
  sessionRows: z.array(z.tuple([z.string(), z.string()])),
  aiRows: z.array(ReplayAiSchema),
  notes: z.array(z.object({ id: IdSchema, bar: z.number().int(), text: z.string() }).strict()),
});
/** One recorded execution event, exactly as the money path wrote it (SM-P6-07, directive §7). `at` is the stored
 *  timestamp, verbatim — replay overlays these on the tape; it never adjusts, interpolates or invents one. */
export const ExecutionEventSchema = z
  .object({
    at: z.number().int(),
    kind: z.enum([
      'claimed',
      'dispatched',
      'confirmed',
      'done',
      'rejected',
      'unconfirmed',
      'reconciled',
      'entry',
      'exit',
    ]),
    symbol: z.string(),
    side: z.enum(['buy', 'sell']).nullable(),
    price: z.number().nullable(),
    quantity: z.number().nullable(),
    detail: z.string(),
    source: z.enum(['order_intent', 'trade']),
  })
  .strict();

/** The tape behind a replay: a REFERENCE to stored candles, pinned by provider and dataVersion so the replay
 *  record can say exactly which bars it played (directive §7's retention list). Null segment + a stated reason is
 *  the honest answer when the tape does not exist — never a synthesized one. */
export const ReplayExecutionSchema = withMeta({
  events: z.array(ExecutionEventSchema),
  segment: z
    .object({
      symbol: z.string(),
      resolution: z.string(),
      from: z.number().int(),
      to: z.number().int(),
      timezone: z.literal('UTC'),
      provider: z.string(),
      dataVersion: z.string(),
      bars: z.number().int(),
    })
    .strict()
    .nullable(),
  unavailable: z.string().nullable(),
});

export const ReplayMetaSchema = withMeta({ rows: z.array(KvSchema) });
export const ReplayAnalysisSchema = withMeta({
  bundleId: IdSchema,
  cards: z.array(ReplayAiSchema),
  summary: z.string(),
});
export const BacktestRunSchema = withMeta({
  runId: IdSchema,
  state: z.enum(['simulating', 'done']),
  book: z.string(),
  assumptions: z.array(z.string()),
  /** The deterministic candle engine's run record (SM-P6-05). Null when the run predates the engine or fell back
   *  to the statistical simulation because no stored candles cover the request — stated, never silent. */
  engine: z
    .object({
      engineVersion: z.string(),
      dataVersion: z.string(),
      provider: z.string(),
      symbol: z.string(),
      baseResolution: z.string(),
      higherResolutions: z.array(z.string()),
      startAt: z.number().int(),
      endAt: z.number().int(),
      barCount: z.number().int(),
      gapCount: z.number().int(),
      strategy: z.object({ id: z.string(), version: z.string() }).strict(),
      summary: z
        .object({
          trades: z.number().int(),
          winRate: z.number(),
          netPnl: z.number(),
          grossPnl: z.number(),
          fees: z.number(),
          maxDrawdown: z.number(),
          maxDrawdownPct: z.number(),
          endingEquity: z.number(),
          expectancyR: z.number().nullable(),
          rejectedOrders: z.number().int(),
        })
        .strict(),
      equityCurve: z.array(z.object({ at: z.number().int(), equity: z.number() }).strict()).max(500),
      /** Wall time of the run — metadata for §11 gate 6's record, never an input; determinism is over results. */
      durationMs: z.number().int().nonnegative().optional(),
    })
    .strict()
    .nullish(),
  results: z
    .object({
      trades: z.number().int(),
      winRate: z.number(),
      expectancyR: z.number(),
      maxDrawdownR: z.number(),
      netR: z.number(),
    })
    .strict()
    .nullable(),
});

// ── End-of-day report: immutable once issued ──
export const EodReportSchema = withMeta({
  reportId: IdSchema,
  day: IsoDateSchema,
  session: EodSessionSchema,
  kpis: z.array(KpiSchema),
  hourly: z.array(z.tuple([z.string(), z.number(), z.boolean()])),
  rules: z.array(z.object({ t: z.string(), ok: z.boolean() }).strict()),
  autoGenerated: z.boolean(),
  issuedAt: IsoDateTimeSchema,
  artifactUrl: z.string().nullable(),
});
export const PlannedVsActualSchema = withMeta({
  planned: z.array(z.string()),
  actual: z.array(z.object({ strategy: z.string(), trades: z.number().int(), onPlan: z.boolean() }).strict()),
  adherencePct: z.number(),
  bypassHistory: z.array(EodBypassSessionSchema),
});

// ── Account progress ──
export const CurvePointSchema = z
  .object({ day: IsoDateSchema, equity: MoneySchema, balance: MoneySchema })
  .strict();
export const MilestoneSchema = z
  .object({
    id: IdSchema,
    label: z.string(),
    target: z.number(),
    progress: z.number(),
    reachedAt: IsoDateTimeSchema.nullable(),
  })
  .strict();
export const ChallengeStatusSchema = withMeta({ accounts: z.array(ApAccountSchema) });
export const PredictiveSchema = withMeta({
  accountId: IdSchema,
  source: z.enum(['llm', 'statistical']),
  cached: z.boolean(),
  probabilityPass: z.number().min(0).max(1),
  projectedDays: z.number().int().nullable(),
  narrative: z.string(),
  generatedAt: IsoDateTimeSchema,
});

export const reviewProcedures = defineProcedures({
  'review.journal.create': mutation(
    'Create a journal entry (text or voice; auto-trade-tag; commits ≤ 3s after speech ends)',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        day: IsoDateSchema.optional(),
        kind: JournalEntryKindSchema,
        mode: JournalEntryModeSchema,
        text: z.string().min(1).max(5000),
        tags: z.array(z.string()).max(20).optional(),
        emotionTags: z.array(z.string()).max(10).optional(),
        tradeId: z.number().int().nullable().optional(),
        audioRef: z.object({ id: IdSchema, durationS: z.number() }).strict().nullable().optional(),
      })
      .strict(),
    JournalEntrySchema,
  ),
  'review.journal.bySession': query(
    'Entries of one session (pre/post blocks + checklist log)',
    z.object({ sessionId: IdSchema }).strict(),
    withMeta({ items: z.array(JournalEntrySchema) }),
  ),
  'review.journal.list': query(
    'Journal entries (cursor) + calendar days + reviewed sessions',
    CursorInputSchema.extend({ from: IsoDateSchema.optional(), to: IsoDateSchema.optional() }).optional(),
    JournalListSchema,
  ),
  'review.journal.tag': mutation(
    'Tag/untag an entry',
    z
      .object({ idempotencyKey: IdempotencyKeySchema, id: IdSchema, tags: z.array(z.string()).max(20) })
      .strict(),
    JournalEntrySchema,
  ),
  'review.journal.attachScreenshot': mutation(
    'Attach a pasted screenshot / pulled chart',
    z.object({ idempotencyKey: IdempotencyKeySchema, id: IdSchema, screenshotId: IdSchema }).strict(),
    JournalEntrySchema,
  ),
  'review.journal.patterns': query(
    'Mined behavioural patterns (feeds the Behavioral pillar)',
    z.void(),
    withMeta({ items: z.array(JournalPatternSchema) }),
  ),

  'review.replay.prepare': mutation(
    'Prepare a replay bundle for a day',
    z.object({ idempotencyKey: IdempotencyKeySchema, day: IsoDateSchema }).strict(),
    ReplayBundleSchema,
  ),
  'review.replay.get': query(
    'Replay bundle for a day (session, grading, meta rows)',
    z.object({ day: IsoDateSchema.optional() }).strict().optional(),
    ReplayBundleSchema,
  ),
  'review.replay.annotate': mutation(
    'Add a note at a bar',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        bundleId: IdSchema,
        bar: z.number().int().nonnegative(),
        text: z.string().min(1).max(500),
      })
      .strict(),
    ReplayBundleSchema,
  ),
  'review.replay.analyse': mutation(
    'AI analysis card for a bundle',
    z.object({ idempotencyKey: IdempotencyKeySchema, bundleId: IdSchema }).strict(),
    ReplayAnalysisSchema,
  ),
  'review.replay.execution': query(
    'The captured execution-event log behind a day or a trade, with its tape reference (SM-P6-07)',
    z
      .object({ day: IsoDateSchema.optional(), tradeId: IdSchema.optional() })
      .strict()
      .refine((v) => v.day !== undefined || v.tradeId !== undefined, 'name a day or a trade'),
    ReplayExecutionSchema,
  ),
  'review.replay.tradesForDay': query(
    'Trades of a day (no-trades-day empty state)',
    z.object({ day: IsoDateSchema }).strict(),
    withMeta({ rows: z.array(ExTradeRowSchema) }),
  ),
  'review.backtest.run': mutation(
    'Run a backtest against a playbook book',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        book: z.string(),
        days: z.number().int().min(5).max(365),
        riskPerTradeR: z.number().positive().max(5),
        /** Candle-engine targeting (SM-P6-05). Defaults let the existing screen run the engine unchanged. */
        symbol: z.string().min(3).max(20).optional(),
        resolution: z.enum(['1', '5', '15', '60', '240', '1D']).optional(),
        higherResolutions: z
          .array(z.enum(['5', '15', '60', '240', '1D']))
          .max(3)
          .optional(),
      })
      .strict(),
    BacktestRunSchema,
  ),
  'review.backtest.results': query(
    'Backtest books + runs',
    z.object({ runId: IdSchema.optional() }).strict().optional(),
    withMeta({ books: z.record(z.string(), BtBookSchema), runs: z.array(BacktestRunSchema) }),
  ),

  'review.eod.list': query(
    'Issued EOD reports (rail) + bypass history',
    CursorInputSchema.optional(),
    pageOf(EodSessionSchema).extend({ bypassHistory: z.array(EodBypassSessionSchema) }),
  ),
  'review.eod.get': query(
    'One EOD report',
    z.object({ day: IsoDateSchema.optional() }).strict().optional(),
    EodReportSchema,
  ),
  'review.eod.plannedVsActual': query(
    'Plan-vs-actual block of a report',
    z.object({ day: IsoDateSchema.optional() }).strict().optional(),
    PlannedVsActualSchema,
  ),

  'review.progress.curve': query(
    'Equity curve points',
    z.object({ accountId: IdSchema.optional() }).strict().optional(),
    withMeta({ points: z.array(CurvePointSchema) }),
  ),
  'review.progress.milestones': query(
    'Milestones',
    z.object({ accountId: IdSchema.optional() }).strict().optional(),
    withMeta({ items: z.array(MilestoneSchema) }),
  ),
  'review.progress.challengeStatus': query(
    'Challenge target/drawdown status per account',
    z.void(),
    ChallengeStatusSchema,
  ),
  'review.progress.predictive': query(
    'Predictive pass-probability card (LLM with statistical fallback)',
    z.object({ accountId: IdSchema }).strict(),
    PredictiveSchema,
  ),
  'review.progress.predictiveHistory': query(
    'Predictive history',
    z.object({ accountId: IdSchema }).strict(),
    withMeta({ items: z.array(PredictiveSchema) }),
  ),
});
export type ReviewFamilyClient = RouterShape<typeof reviewProcedures>;

export const reviewFamily = defineFamily({
  name: 'review',
  title: 'Review',
  screens: ['Journal', 'Trade Replay', 'End-of-Day Report', 'Account Progress'],
  procedures: reviewProcedures,
  binding: 'simulated',
});
